import {
  type AdminAsset,
  type AdminPageBlock,
  type ReplacePageBlocksRequest,
  type JournalPostSummary,
  type ShootSummary,
  type SocialLink,
  type VersionedPage,
  type VersionedPricing,
} from '@diana/contracts';
import { useFormPageViewport } from '../../ui/use-form-page-viewport.js';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useEditorCommandRegistration } from '../../app/EditorCommandContext.js';
import { ConflictDialog } from '../../components/ConflictDialog.js';
import { readRevisionConflict } from '../../lib/use-conflict.js';
import { useAutosave } from '../../lib/use-autosave.js';
import { EditorHeading } from '../settings/SettingsEditor.js';
import { PageSectionOverlayContext } from './PageSectionsWorkspace.js';
import { PageSettingsPanel } from './PageSettingsPanel.js';
import { HomepageEditor } from './HomepageEditor.js';
import { PageSeoFields, StructuredPageEditor } from './StructuredPageEditor.js';

type PageEditorProps = {
  page: VersionedPage;
  assets?: readonly AdminAsset[];
  shoots?: readonly ShootSummary[];
  journal?: readonly JournalPostSummary[];
  pricing?: VersionedPricing;
  socialLinks?: readonly SocialLink[];
  save(request: ReplacePageBlocksRequest): Promise<VersionedPage>;
};

export function PageEditor({
  page,
  assets = [],
  shoots = [],
  journal = [],
  pricing,
  socialLinks = [],
  save,
}: PageEditorProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const conflictRoot = useRef<HTMLDivElement>(null);
  const autosave = useAutosave({
    initialValue: {
      blocks: page.blocks,
      payload: page.payload,
      siteAccess: page.siteAccess,
    },
    initialRevision: page.revision,
    save: async ({ value, expectedRevision }) => {
      const result = await save({ expectedRevision, ...value });
      return {
        value: {
          blocks: result.blocks,
          payload: result.payload,
          siteAccess: result.siteAccess,
        },
        revision: result.revision,
      };
    },
    readConflict: (error) =>
      readRevisionConflict(error, (current) =>
        isPage(current)
          ? {
              blocks: current.blocks,
              payload: current.payload,
              siteAccess: current.siteAccess,
            }
          : null,
      ),
  });
  const draft = autosave.snapshot.value;
  const blocks = draft.blocks;
  useEditorCommandRegistration({
    status: autosave.snapshot.status,
    save: () => void autosave.flush(),
  });
  const updateBlocks = (nextBlocks: readonly AdminPageBlock[]) =>
    autosave.edit({
      ...draft,
      blocks: [...nextBlocks],
    });
  const updateBlock = (changed: AdminPageBlock) =>
    updateBlocks(
      blocks.some((block) => block.id === changed.id)
        ? blocks.map((block) => (block.id === changed.id ? changed : block))
        : [...blocks, changed],
    );

  const conflicted = autosave.snapshot.status === 'Conflict';
  useEffect(() => {
    if (!conflicted) return;
    setSettingsOpen(false);
    const frame = requestAnimationFrame(() =>
      conflictRoot.current?.querySelector('button')?.focus(),
    );
    return () => cancelAnimationFrame(frame);
  }, [conflicted]);
  const { pageRef } = useFormPageViewport();
  const title = page.slug === 'home' ? 'Homepage' : capitalize(page.slug);

  return (
    <article className="editor page-editor" ref={pageRef} data-form-page data-page-content-editor>
      <header className="page-editor__header">
        <Link className="dui-back-link" to="/pages" aria-label="Back to Pages">
          <ArrowLeft size={18} aria-hidden="true" /> Pages
        </Link>
        <EditorHeading
          title={title}
          status={autosave.snapshot.status}
          onRetry={() => void autosave.retry()}
        />
        <PageSettingsPanel
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          conflicted={conflicted}
          title={title}
          status={autosave.snapshot.status}
          onRetry={() => void autosave.retry()}
        >
          <PageSeoFields
            page={page}
            payload={draft.payload}
            siteAccess={draft.siteAccess}
            assets={assets}
            onChange={(patch) => autosave.edit({ ...draft, ...patch })}
          />
        </PageSettingsPanel>
      </header>
      <PageSectionOverlayContext.Provider value={settingsOpen || conflicted}>
        {page.kind === 'Home' ? (
          <HomepageEditor
            blocks={blocks}
            assets={assets}
            shoots={shoots}
            socialLinks={socialLinks}
            onChange={updateBlock}
          />
        ) : (
          <StructuredPageEditor
            page={page}
            blocks={blocks}
            assets={assets}
            shoots={shoots}
            journal={journal}
            pricing={pricing}
            onBlockChange={updateBlock}
            onBlocksChange={updateBlocks}
          />
        )}
      </PageSectionOverlayContext.Provider>
      <div ref={conflictRoot}>
        <ConflictDialog
          open={autosave.snapshot.status === 'Conflict'}
          onAcceptServer={autosave.acceptServerVersion}
          onReapplyLocal={autosave.reapplyLocal}
          onCopyLocal={() =>
            void navigator.clipboard?.writeText(JSON.stringify(autosave.copyLocal(), null, 2))
          }
        />
      </div>
    </article>
  );
}

function isPage(value: unknown): value is VersionedPage {
  return !!value && typeof value === 'object' && 'blocks' in value && 'revision' in value;
}

function capitalize(value: string) {
  return value ? `${value[0]!.toLocaleUpperCase()}${value.slice(1)}` : value;
}
